// Connects the offline deck outbox (C4.6) to the deck editor's save queue
// and the deck lists. Installed once by main.tsx (after the runtime).
//
//  - The save queue's offline port: while the API is unreachable, the
//    session is not usable, or the outbox still holds a deck's edit, the
//    editor RECORDS its newest text in the outbox (one writer per deck); the
//    scheduler sends it. Rejected (read-only): no edit is accepted at all.
//  - A landed attempt (`done`) committed for THIS tab's owner and
//    incarnation: the acknowledged row reaches every deck list
//    (broadcastPersisted, which also updates the offline copy) and the
//    editor adopts it. An event for another owner or incarnation (a switch
//    happened meanwhile) is ignored (finding 10).
//  - A conflict, a missing row or a permanent failure: the outbox KEEPS the
//    draft and the stopped attempt (finding 3) until the user chooses (the
//    editor's own flow, or the review panel); an open editor only stops.
//  - C1 recovered: retries the save queue stopped while offline.
import { subscribeDeckOutbox, discardDeckDraft } from './deckOutbox'
import {
  deckHasOfflineWorkSync, offlineRuntimeActive, recordOfflineDeckEdit, refreshPendingCounts,
} from './runtime'
import { currentWriterCtx } from './offlineContext'
import { getIdentity } from './identity'
import { connectivityState, onConnectivityEvent } from './connectivity'
import { accountOwner, ownerSubject } from './identityMachine'
import { CONN, CONN_EVENT, GUEST_OWNER, MODE } from './offline.codes'
import { OUTBOX_STOP } from './offlineUi.codes'
import {
  broadcastPersisted, peekEditorSaveController, retryAllEditorSaves, setEditorOfflinePort,
} from '../../components/decks/editor/editorSaveQueue'
import { mapRowToDeck } from '../../hooks/useSavedDecks'

let installed = false

export function installDeckOutboxBridge(): void {
  if (installed) return
  installed = true
  setEditorOfflinePort({
    shouldQueue: (scope, deckId) => {
      if (!offlineRuntimeActive() || scope === GUEST_OWNER) return false
      const id = getIdentity()
      if (id.offlineOwner !== accountOwner(scope)) return false
      return connectivityState() !== CONN.online || id.mode !== MODE.active || deckHasOfflineWorkSync(deckId)
    },
    canEdit: (scope) => {
      if (!offlineRuntimeActive() || scope === GUEST_OWNER) return true
      const id = getIdentity()
      return id.offlineOwner !== accountOwner(scope) || id.allowed.edit
    },
    isOffline: () => offlineRuntimeActive() && connectivityState() !== CONN.online,
    recordEdit: (input) => recordOfflineDeckEdit(input),
    // An explicit Discard / Use current in the editor: the user's choice.
    discard: async (deckId) => {
      const ctx = currentWriterCtx()
      if (!ctx) return
      await discardDeckDraft(ctx, deckId)
      await refreshPendingCounts()
    },
  })
  subscribeDeckOutbox((e) => {
    const ctx = currentWriterCtx()
    // Only an event committed for this tab's owner and incarnation now.
    if (!ctx || e.ownerKey !== ctx.ownerKey || e.incarnation !== ctx.incarnation) return
    const uid = ownerSubject(ctx.ownerKey)
    if (!uid) return
    if (e.type === 'done') {
      const deck = mapRowToDeck(e.row as unknown as Parameters<typeof mapRowToDeck>[0])
      broadcastPersisted(uid, deck)
      peekEditorSaveController(uid, e.deckId)?.adoptOutboxRow(deck, e.cleared)
      void refreshPendingCounts()
      return
    }
    // Stopped for the user: nothing is deleted here (the review panel and
    // the editor's own flow resolve it explicitly).
    void refreshPendingCounts()
    peekEditorSaveController(uid, e.deckId)?.outboxStopped(e.type === OUTBOX_STOP.missing ? OUTBOX_STOP.missing : OUTBOX_STOP.stale)
  })
  onConnectivityEvent((ev) => { if (ev === CONN_EVENT.recovered) retryAllEditorSaves() })
}

/** Test seam: the next install wires again (the outbox listeners were reset). */
export function __resetDeckOutboxBridgeForTests(): void {
  installed = false
}
